import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, forkJoin } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { Notification, NotificationType, NotificationBadge } from '../models/notification.model';
import { UsersService } from './users.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private http: HttpClient, private usersService: UsersService) {
    // Don't automatically load notifications - wait for explicit call
  }

  private loadNotifications(): void {
    this.getAllNotifications().subscribe({
      next: (notifications) => {
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount();
      },
      error: () => {
        // Fallback to empty array if API fails
        this.notificationsSubject.next([]);
        this.updateUnreadCount();
      }
    });
  }

  getAllNotifications(): Observable<Notification[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/Notifications`)
      .pipe(
        switchMap((apiNotifications: any[]) => {
          // Get unique user IDs from notifications
          const uniqueUserIds = [...new Set(apiNotifications.map(n => n.userId))];
          
          // Fetch user details for all unique users
          const userRequests = uniqueUserIds.map(userId => 
            this.usersService.getUserById(userId).pipe(
              map(user => ({ userId, user })),
              catchError(() => of({ userId, user: { fullName: 'Unknown User' } }))
            )
          );
          
          return forkJoin(userRequests).pipe(
            map(userDetails => {
              // Create a map of userId to user details
              const userMap = userDetails.reduce((acc, { userId, user }) => {
                acc[userId] = user;
                return acc;
              }, {} as any);
              
              // Map notifications with user details
              return apiNotifications.map(notification => 
                this.mapApiNotificationToModel(notification, userMap[notification.userId])
              );
            })
          );
        }),
        catchError(() => {
          return throwError(() => new Error('Failed to fetch notifications'));
        })
      );
  }

  private mapApiNotificationToModel(apiNotification: any, userDetails?: any): Notification {
    const userName = userDetails?.fullName || '';
    const enhancedMessage = this.enhanceMessageWithUser(apiNotification.message, userName);
    
    return {
      id: apiNotification.id?.toString() || '',
      type: this.mapApiTypeToNotificationType(apiNotification.type),
      title: apiNotification.title || '',
      message: enhancedMessage,
      timestamp: apiNotification.createdOn ? new Date(apiNotification.createdOn) : new Date(),
      isRead: apiNotification.isRead || false,
      actionUrl: undefined,
      icon: this.getIconForType(this.mapApiTypeToNotificationType(apiNotification.type))
    };
  }

  private enhanceMessageWithUser(originalMessage: string, userName: string): string {
    // If no user name or message already contains the user name, return as is
    if (!userName || originalMessage.toLowerCase().includes(userName.toLowerCase())) {
      return originalMessage;
    }
    
    // Otherwise, prepend the user name to the message
    return `${userName} ${originalMessage}`;
  }

  private mapApiTypeToNotificationType(apiType: number | string): NotificationType {
    // Handle numeric types from API
    const numericTypeMap: { [key: number]: NotificationType } = {
      1: 'success',
      2: 'warning',
      3: 'error',
      4: 'info',
      5: 'system'
    };
    
    // Handle string types (fallback)
    const stringTypeMap: { [key: string]: NotificationType } = {
      'success': 'success',
      'warning': 'warning',
      'error': 'error',
      'info': 'info',
      'system': 'system',
      'notification': 'info',
      'alert': 'warning'
    };
    
    if (typeof apiType === 'number') {
      return numericTypeMap[apiType] || 'info';
    }
    
    return stringTypeMap[apiType?.toLowerCase()] || 'info';
  }

  private getIconForType(type: NotificationType): string {
    const iconMap: { [key in NotificationType]: string } = {
      'success': 'pi pi-check-circle',
      'warning': 'pi pi-exclamation-triangle',
      'error': 'pi pi-times-circle',
      'info': 'pi pi-info-circle',
      'system': 'pi pi-star'
    };
    return iconMap[type] || 'pi pi-bell';
  }

  getNotifications(): Notification[] {
    return this.notificationsSubject.value;
  }

  getUnreadCount(): number {
    return this.notificationsSubject.value.filter(n => !n.isRead).length;
  }

  markAsRead(notificationId: string): Observable<any> {
    const notifications = this.notificationsSubject.value;
    const notification = notifications.find(n => n.id === notificationId);
    
    if (notification) {
      // Optimistically update the UI first
      notification.isRead = true;
      this.notificationsSubject.next([...notifications]);
      this.updateUnreadCount();
      
      // Call API to mark as read on server
      return this.http.post(`${environment.apiUrl}/Notifications/${notificationId}/read`, {})
        .pipe(
          catchError(() => {
            // Revert the optimistic update on error
            notification.isRead = false;
            this.notificationsSubject.next([...notifications]);
            this.updateUnreadCount();
            return throwError(() => new Error('Failed to mark notification as read'));
          })
        );
    }
    
    return throwError(() => new Error('Notification not found'));
  }

  markAllAsRead(): Observable<any> {
    const notifications = this.notificationsSubject.value;
    const originalNotifications = [...notifications];
    
    // Optimistically update the UI first
    const updatedNotifications = notifications.map(n => ({ ...n, isRead: true }));
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount();
    
    // Call API to mark all as read on server
    return this.http.post(`${environment.apiUrl}/Notifications/markallread`, {})
      .pipe(
        catchError(() => {
          // Revert the optimistic update on error
          this.notificationsSubject.next(originalNotifications);
          this.updateUnreadCount();
          return throwError(() => new Error('Failed to mark all notifications as read'));
        })
      );
  }



  clearAllNotifications(): Observable<any> {
    const originalNotifications = [...this.notificationsSubject.value];
    
    // Optimistically update the UI first
    this.notificationsSubject.next([]);
    this.updateUnreadCount();
    
    // Call API to clear all notifications on server
    return this.http.delete(`${environment.apiUrl}/Notifications/clearall`)
      .pipe(
        catchError(() => {
          // Revert the optimistic update on error
          this.notificationsSubject.next(originalNotifications);
          this.updateUnreadCount();
          return throwError(() => new Error('Failed to clear all notifications'));
        })
      );
  }

  private updateUnreadCount(): void {
    const unreadCount = this.getUnreadCount();
    this.unreadCountSubject.next(unreadCount);
  }

  getNotificationBadge(): NotificationBadge {
    const notifications = this.notificationsSubject.value;
    const unreadCount = notifications.filter(n => !n.isRead).length;
    return {
      count: unreadCount,
      hasUnread: unreadCount > 0
    };
  }

  refreshNotifications(): Observable<Notification[]> {
    return this.getAllNotifications().pipe(
      tap((notifications) => {
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount();
      }),
      catchError(() => {
        return throwError(() => new Error('Failed to refresh notifications'));
      })
    );
  }

  loadNotificationsOnLogin(): void {
    this.loadNotifications();
  }

  clearNotifications(): void {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }
}
